import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../../common/hooks/hooks";
import { useGetWishlistQuery } from "../../api/wishlistApi";
import { useGetProductsByIdsQuery } from "../../../product/api/productApi";
import { closeMultiPanelOffcanvas } from "../../../../../common/features/multiPanelOffcanvas/multiPanelOffcanvasSlice";
import { sortProductsByIds } from "../../helpers/sortProductsByIds";
import Icon from "../../../../../common/components/Icon/Icon";
import PanelItem from "../../../product/components/common/ProductPanelItem/ProductPanelItem";
import PanelItemSkeleton from "../../../product/components/common/ProductPanelItem/ProductPanelItemSkeleton";
import PanelFeaturedProducts from "../../../featured/components/Panel/PanelFeaturedProducts";
import PanelFeaturedCategories from "../../../featured/components/Panel/PanelFeaturedCategories";
import PanelFeaturedTags from "../../../featured/components/Panel/PanelFeaturedTags";

const WishlistPanel: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    data: itemIds,
    isLoading: isWishlistLoading,
    isError: isWishlistError,
  } = useGetWishlistQuery();

  // Fetch Products based on IDs
  // Skip if no IDs to avoid empty API call
  const shouldFetchProducts = !!itemIds && itemIds.length > 0;
  const {
    data: products,
    isLoading: areProductsLoading,
    isError: areProductsError,
  } = useGetProductsByIdsQuery(itemIds || [], {
    skip: !shouldFetchProducts,
  });

  // Create a combined loading state
  const isComponentLoading =
    isWishlistLoading || areProductsLoading || !areTranslationsReady;
  const isError = isWishlistError || areProductsError;

  const sortedProducts = useMemo(() => {
    return sortProductsByIds(products, itemIds);
  }, [itemIds, products]);

  const wishlistUrl = window.commonVariables?.wishlistUrl || "/wishlist";

  // State 1: Loading (show skeleton)
  if (isComponentLoading) {
    return (
      <div className="wishlist-panel">
        <div className="wishlist-panel__body">
          <ul className="wishlist-panel__items">
            {Array.from({ length: 3 }).map((_, index) => (
              <PanelItemSkeleton key={index} />
            ))}
          </ul>
        </div>
      </div>
    );
  }

  // State 2: Error
  if (isError) {
    return (
      <div className="wishlist-panel">
        <p className="wishlist-panel__message wishlist-panel__message--error">
          {t("wishlistOffcanvas.error", "Error loading wishlist.")}
        </p>
      </div>
    );
  }

  // State 3: Wishlist Empty
  if (!itemIds || itemIds.length === 0) {
    return (
      <div className="wishlist-panel">
        <div className="wishlist-panel__body">
          <p className="wishlist-panel__message wishlist-panel__message--empty">
            {t("wishlistOffcanvas.empty", "Your wishlist is empty.")}
          </p>

          <PanelFeaturedProducts />
          <PanelFeaturedCategories />
          <PanelFeaturedTags />
        </div>
      </div>
    );
  }

  // State 5: Success with Results
  return (
    <div className="wishlist-panel">
      <div className="wishlist-panel__body">
        {/* State 4: Fetching state intentionally ignored to avoid flicker */}
        <ul className="wishlist-panel__items">
          {sortedProducts.map((product) => (
            <PanelItem
              key={product.id}
              product={product}
              onItemClick={() => dispatch(closeMultiPanelOffcanvas())}
              showStockStatus={true}
            />
          ))}
        </ul>
      </div>
      <div className="wishlist-panel__footer">
        <a
          href={wishlistUrl}
          className="wishlist-panel__view-btn"
          onClick={() => dispatch(closeMultiPanelOffcanvas())}
        >
          <Icon iconName="heart-filled" className="icon--filled" />
          {t("wishlistOffcanvas.viewWishlist", "View Wishlist")}
        </a>
      </div>
    </div>
  );
};

export default WishlistPanel;
